import { BeaconBanner, BeaconProductResult, trackImpression } from "./beacon"

/**
 * Impression tracking (Beacon 2.0)
 * ---
 * A product is counted as "impressed" when it is:
 *   - at least 70% visible in the viewport
 *   - visible for at least 1000ms continuously
 *   - not hidden via display:none, visibility:hidden or opacity:0
 *
 * Each product is reported at most once per response (i.e. per render cycle).
 *
 * Reference: https://docs.searchspring.com/reference/impression-tracking-guide
 */

const VISIBILITY_RATIO = 0.7
const VISIBLE_DURATION_MS = 1000

export type ImpressionModule =
  | "search"
  | "category"
  | "autocomplete"
  | "recommendations"

export type ImpressionObserverOptions = {
  module: ImpressionModule
  responseId: string
  tag?: string
  /** Coalesce multiple products into a single batched request. */
  batchWindowMs?: number
}

function isElementVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return true
  const style = window.getComputedStyle(el)
  if (style.display === "none") return false
  if (style.visibility === "hidden") return false
  if (style.opacity === "0") return false
  return true
}

export class BeaconImpressionObserver {
  private module: ImpressionModule
  private responseId: string
  private tag?: string
  private batchWindowMs: number

  private observer: IntersectionObserver
  private timers = new WeakMap<Element, number>()
  private reportedKeys = new Set<string>()
  private pending: BeaconProductResult[] = []
  private pendingBanners: BeaconBanner[] = []
  private flushTimer: number | null = null
  private resultMap = new WeakMap<Element, BeaconProductResult>()
  private bannerMap = new WeakMap<Element, BeaconBanner>()

  private flushOnPagehide = () => this.flush()

  constructor(options: ImpressionObserverOptions) {
    this.module = options.module
    this.responseId = options.responseId
    this.tag = options.tag
    this.batchWindowMs = options.batchWindowMs ?? 200

    this.observer = new IntersectionObserver(
      (entries) => this.handleEntries(entries),
      { threshold: VISIBILITY_RATIO }
    )

    // Flush pending impressions before the page is unloaded so navigation
    // away (link click, tab close) does not lose data. sendBeacon survives.
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this.flushOnPagehide)
    }
  }

  observeProduct(element: Element, result: BeaconProductResult) {
    if (!element || !this.responseId) return
    const key = this.keyOf(result)
    if (this.reportedKeys.has(key)) return
    this.resultMap.set(element, result)
    this.observer.observe(element)
  }

  observeBanner(element: Element, banner: BeaconBanner) {
    if (!element || !this.responseId) return
    const key = `banner:${banner.uid}`
    if (this.reportedKeys.has(key)) return
    this.bannerMap.set(element, banner)
    this.observer.observe(element)
  }

  disconnect() {
    this.observer.disconnect()
    if (this.flushTimer) {
      window.clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.flushOnPagehide)
    }
    this.flush()
  }

  private keyOf(result: BeaconProductResult) {
    return `${result.parentId}:${result.uid}`
  }

  private handleEntries(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      const el = entry.target
      if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_RATIO) {
        if (!isElementVisible(el)) continue
        if (this.timers.has(el)) continue
        const timer = window.setTimeout(() => {
          this.timers.delete(el)
          if (!isElementVisible(el)) return
          this.report(el)
        }, VISIBLE_DURATION_MS)
        this.timers.set(el, timer)
      } else {
        const timer = this.timers.get(el)
        if (timer !== undefined) {
          window.clearTimeout(timer)
          this.timers.delete(el)
        }
      }
    }
  }

  private report(el: Element) {
    const result = this.resultMap.get(el)
    const banner = this.bannerMap.get(el)

    if (result) {
      const key = this.keyOf(result)
      if (this.reportedKeys.has(key)) return
      this.reportedKeys.add(key)
      this.pending.push({ type: result.type ?? "product", ...result })
    } else if (banner) {
      const key = `banner:${banner.uid}`
      if (this.reportedKeys.has(key)) return
      this.reportedKeys.add(key)
      this.pendingBanners.push(banner)
    } else {
      return
    }

    this.observer.unobserve(el)
    this.scheduleFlush()
  }

  private scheduleFlush() {
    if (this.flushTimer !== null) return
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null
      this.flush()
    }, this.batchWindowMs)
  }

  private flush() {
    if (!this.pending.length && !this.pendingBanners.length) return
    const results = this.pending
    const banners = this.pendingBanners
    this.pending = []
    this.pendingBanners = []
    trackImpression(this.module, this.responseId, results, banners, {
      tag: this.tag,
    })
  }
}
